/**
 * OGraf graphics in the browser (0.93.0): one sandboxed, network-less frame running the same host page as the
 * renderer (buildOgrafHost), holding any number of graphic instances stacked by z (track order).
 *
 * The frame is /graphics-frame.html in <iframe sandbox="allow-scripts">: an opaque origin with no access to PRISM's
 * cookies, storage or API. The app fetches each package's files with the session and posts the bytes in; the host
 * makes its own blob: URLs (a blob URL made on PRISM's origin cannot be used from the opaque frame). Instances are
 * mounted (load + setActionsSchedule) when they appear or change and unmounted when they go.
 *
 * Seeking is coalesced: while one render request (goToTime on every instance, awaited) is in flight, only the
 * latest request is kept, and it is sent as soon as the frame answers. The sequence viewer calls seek on every
 * animation frame during playback, so graphics stay locked to the picture's frame. goToTime may take a while (a
 * graphic with video waits until its video frames are decoded, up to 5 s); the next request simply waits for it.
 *
 * Failures (0.94.0): a graphic whose load or goToTime throws or answers anything but 2xx is hidden by the host (no
 * frame of it is shown) and reported by instance; the frame shows an error naming that graphic, with its
 * statusMessage, until it draws again.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { buildOgrafHost, graphicPlacement, ografLayoutFor, type GraphicData, type OgrafManifest, type OgrafScheduleEntry, type Rational } from '@prism/contracts';
import type { OgrafPackageData } from './ografPackage';

export interface OgrafMount {
  instance: string;
  pkg: OgrafPackageData;
  manifest: OgrafManifest;
  data: GraphicData;
  schedule: OgrafScheduleEntry[];
  /** The sequence's rate (renderCharacteristics, and the schedule's frame grid). */
  frameRate: Rational;
  /** Stacking order (track order: later graphics on top). */
  z: number;
}

export interface OgrafSeek {
  instance: string;
  /** ms from the instance's clip start. */
  ms: number;
  visible: boolean;
}

export interface OgrafFrameHandle {
  seek(seeks: OgrafSeek[]): void;
}

/**
 * How long one render request may take before the frame gives up on it and shows an error on its graphics. A graphic
 * with video waits up to 5 s for a frame in its own runtime, but that limit is measured with performance.now, which
 * the host freezes; this is the preview's own bound (the renderer has one per frame too).
 */
export const OGRAF_RENDER_TIMEOUT_MS = 10_000;

const signature = (m: OgrafMount, stageHeight: number) => `${m.pkg.key}|${m.z}|${stageHeight}|${m.frameRate.num}/${m.frameRate.den}|${JSON.stringify(m.data)}|${JSON.stringify(m.schedule)}`;

export const OgrafFrame = forwardRef<OgrafFrameHandle, {
  /** The frame's own picture size (the sequence's, or the graphic's for a single preview). */
  stage: { width: number; height: number };
  mounts: OgrafMount[];
  title: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: (message: string | null) => void;
}>(function OgrafFrame({ stage, mounts, title, className, style, onError }, ref) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(1);
  /** Failed graphics by instance ('' for a failure of the frame itself), with the graphic's message. */
  const [errors, setErrors] = useState<Record<string, string>>({});
  const html = useMemo(() => buildOgrafHost(stage), [stage.width, stage.height]); // eslint-disable-line react-hooks/exhaustive-deps
  const htmlRef = useRef(html);
  htmlRef.current = html;
  // Per frame document: what has been posted and mounted, and the seek bookkeeping.
  const s = useRef({ packages: new Set<string>(), mounted: new Map<string, string>(), latest: [] as OgrafSeek[], sent: [] as OgrafSeek[], dirty: false, inFlight: 0, n: 0, ready: false, timer: undefined as ReturnType<typeof setTimeout> | undefined });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const mountsRef = useRef(mounts);
  mountsRef.current = mounts;
  const describe = (id: string, message: string) => {
    const name = mountsRef.current.find((m) => m.instance === id)?.manifest.name;
    return name ? `${name}: ${message}` : message;
  };
  /** Set (message) or clear (null) the errors of some instances. */
  const report = useCallback((changes: Array<[string, string | null]>) => {
    setErrors((prev) => {
      const next = { ...prev };
      for (const [id, message] of changes) { if (message === null) delete next[id]; else next[id] = message; }
      return Object.keys(next).length === Object.keys(prev).length && Object.entries(next).every(([k, v]) => prev[k] === v) ? prev : next;
    });
  }, []);
  const errorText = Object.entries(errors).map(([id, m]) => describe(id, m)).join('; ');
  useEffect(() => { onErrorRef.current?.(errorText || null); }, [errorText]);

  const post = (msg: Record<string, unknown>) => frame.current?.contentWindow?.postMessage(msg, '*');
  const flush = useCallback(() => {
    const st = s.current;
    if (!ready || st.inFlight || !st.dirty) return;
    st.dirty = false;
    const id = st.inFlight = ++st.n;
    st.sent = st.latest;
    post({ type: 'prism:render', id, seeks: st.latest });
    clearTimeout(st.timer);
    st.timer = setTimeout(() => {
      const cur = s.current;
      if (cur.inFlight !== id) return;
      cur.inFlight = 0;
      report(cur.sent.map((x): [string, string] => [x.instance, `did not draw the frame within ${OGRAF_RENDER_TIMEOUT_MS / 1000} s`]));
      flushRef.current();
    }, OGRAF_RENDER_TIMEOUT_MS);
  }, [ready, report]);
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => clearTimeout(s.current.timer), []);

  useImperativeHandle(ref, () => ({
    seek(seeks) {
      s.current.latest = seeks;
      s.current.dirty = true;
      flush();
    },
  }), [flush]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / stage.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [stage.width]);

  // A new host document (another stage size) needs a new frame and a new handshake.
  useEffect(() => {
    setReady(false);
    clearTimeout(s.current.timer);
    s.current = { packages: new Set(), mounted: new Map(), latest: s.current.latest, sent: [], dirty: true, inFlight: 0, n: s.current.n, ready: false, timer: undefined };
  }, [html]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      const m = e.data as { type?: string; id?: number; instance?: string; message?: string; engine?: string; errors?: Array<{ instance: string; message: string }> };
      if (m?.type === 'prism:frame-ready') post({ type: 'prism:load', html: htmlRef.current });
      else if (m?.type === 'prism:ready') {
        // Sent by the frame and by the host's load event: the document is the same, so only the first counts.
        if (s.current.ready) return;
        s.current.ready = true;
        s.current.packages.clear();
        s.current.mounted.clear();
        s.current.inFlight = 0;
        s.current.dirty = true;
        setReady(true);
      } else if (m?.type === 'prism:ograf-done') {
        // A mounted instance shows the latest position, even when no seek came since.
        if (m.instance) report([[m.instance, null]]);
        s.current.dirty = true;
        flush();
      } else if (m?.type === 'prism:rendered') {
        const sent = m.id === s.current.inFlight ? s.current.sent : [];
        if (m.id === s.current.inFlight) { s.current.inFlight = 0; clearTimeout(s.current.timer); }
        const failed = new Map((m.errors ?? []).map((x) => [x.instance, x.message]));
        report([['', null], ...sent.map((x): [string, string | null] => [x.instance, failed.get(x.instance) ?? null]), ...[...failed].filter(([id]) => !sent.some((x) => x.instance === id))]);
        flush();
      } else if (m?.type === 'prism:error') {
        if (m.id === s.current.inFlight) { s.current.inFlight = 0; clearTimeout(s.current.timer); }
        // A mount that failed is forgotten, so a later change mounts it again.
        if (m.instance) s.current.mounted.delete(m.instance);
        report([[m.instance ?? '', String(m.message ?? 'The graphic failed')]]);
        flush();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [flush, report]);

  // Packages and instances: post what is new or changed, unmount what is gone.
  useEffect(() => {
    if (!ready) return;
    const st = s.current;
    const want = new Set(mounts.map((m) => m.instance));
    for (const id of [...st.mounted.keys()]) {
      if (want.has(id)) continue;
      st.mounted.delete(id);
      report([[id, null]]);
      post({ type: 'prism:ograf-unmount', id: ++st.n, instance: id });
    }
    for (const m of mounts) {
      if (!st.packages.has(m.pkg.key)) {
        st.packages.add(m.pkg.key);
        post({ type: 'prism:ograf-package', id: ++st.n, key: m.pkg.key, files: m.pkg.files });
      }
      const sig = signature(m, stage.width * 100_000 + stage.height);
      if (st.mounted.get(m.instance) === sig) continue;
      st.mounted.set(m.instance, sig);
      // 1.0.0-rc.11: laid out at its declared size (or the stage's, in a frame other than 16:9), fitted and centred
      // when that is another shape than the stage, as the export renders it. 1.7.0: a package with a layout per aspect
      // is mounted at the layout of the stage's aspect (ografLayoutFor, exact size; scale 1 when the stage is that
      // size), and load() gets that size in renderCharacteristics.resolution. The layout is chosen once per load(), so
      // a format change (another stage) gives a new host document and a new load.
      const { layout } = ografLayoutFor(m.manifest, stage);
      const size = { width: layout.width, height: layout.height };
      const place = graphicPlacement(size, stage);
      const at = st.latest.find((x) => x.instance === m.instance);
      post({
        type: 'prism:ograf-mount', id: ++st.n, instance: m.instance,
        options: {
          pkg: m.pkg.key, main: m.pkg.main, data: m.data, width: size.width, height: size.height, scale: place.scale, left: place.left, top: place.top, z: m.z,
          frameRate: m.frameRate, schedule: m.schedule, at: at?.ms ?? 0, visible: at?.visible ?? false,
        },
      });
    }
    st.dirty = true;
    flush();
  }, [ready, mounts, stage.width, stage.height, flush, report]);

  // The layouts in use (1.7.0), for the inspector's note and the tests: "9x16" or "16x9,9x16" by instance order.
  const layouts = [...new Set(mounts.map((m) => ografLayoutFor(m.manifest, stage).layout.id))].join(',');
  return (
    <div ref={box} className={className} data-ograf-layouts={layouts || undefined} style={{ position: 'relative', aspectRatio: `${stage.width} / ${stage.height}`, overflow: 'hidden', ...style }}>
      <iframe
        key={html.length + stage.width * 7 + stage.height}
        ref={frame}
        title={title}
        sandbox="allow-scripts"
        src="/graphics-frame.html"
        data-testid="ograf-frame"
        style={{ position: 'absolute', left: 0, top: 0, width: stage.width, height: stage.height, border: 0, transform: `scale(${scale})`, transformOrigin: '0 0', background: 'transparent', colorScheme: 'dark', pointerEvents: 'none' }}
      />
      {Object.keys(errors).length > 0 && (
        <div className="graphic-errors">
          {Object.entries(errors).map(([id, message]) => (
            <div key={id || 'frame'} className="graphic-error" role="alert" data-testid="ograf-error" data-instance={id || undefined}>{describe(id, message)}</div>
          ))}
        </div>
      )}
    </div>
  );
});
